import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core';
import {SharedConstant, SharedService} from '@ems/shared';
import {ConfirmationService} from 'primeng/api';
import {Observable} from 'rxjs';
import {DynamicEnvironment} from '@environment/dynamic-environment';
import { HttpClient } from '@angular/common/http';
import {AuthService} from '@core_services/auth/auth.service';
import {TokenService} from '@core_services/token.service';
import {LocalStorageService} from '@core_services/local-storage.service';
import { HostListener } from '@angular/core';
import {ShellConstant} from '@shell_components/constants/shellConstants';
import { ConfirmDialog } from 'primeng/confirmdialog';
import * as temp from 'moment';
const moment = temp['default'];

@Component({
  selector: 'app-global-timeout',
  templateUrl: './global-timeout.component.html'
})
export class GlobalTimeoutComponent implements OnInit, OnDestroy {

  @ViewChild('cd') cd: ConfirmDialog;

  sharedConstant = SharedConstant;
  logOut = this.sharedConstant.globalTimeoutDialog.logOut;
  stayLoggedIn = this.sharedConstant.globalTimeoutDialog.stayLoggedIn;
  countDown: any;
  counter = 180;
  showHtmlTimer: string;
  clearTime: any = 0;
  timeleft: any;
  environment = new DynamicEnvironment();
  shellConstant = ShellConstant;
  dialogActive = false; // Track if dialog is already shown to prevent multiple instances
  private readonly DIALOG_SHOW_BEFORE_EXPIRY_MS = ShellConstant.sessionConstants.warningDialogInMinutes * 60 * 1000;
  private lastActivityCheckTime = 0; // Throttle token-expiry checks on user activity events
  private silentRefreshInProgress = false; // Prevent concurrent silent refresh + interceptor refresh
  private loggingOut = false; // Prevent any token operations once logout has started

  constructor(public confirmationService: ConfirmationService, public sharedService: SharedService, public authService: AuthService,
              private httpService: HttpClient, private tokenService: TokenService, private localStorageService: LocalStorageService) {
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:onmousemove', ['$event'])
  @HostListener('document:mouseover', ['$event'])
  @HostListener('document:keypress', ['$event'])
  @HostListener('document:onkeypress', ['$event'])
  @HostListener('document:keyCode', ['$event'])
  @HostListener('document:keys', ['$event'])
  @HostListener('document:click', ['$event'])
  @HostListener('document:wheel', ['$event'])
  @HostListener('document:keydown', ['$event'])
  @HostListener('document:keyup', ['$event'])
  startTimer(event: KeyboardEvent) {
    if (!event) {
      return;
    }

    // On user activity: check if token is nearing expiry and refresh silently.
    // Throttled to once every 30 seconds to avoid expensive checks on every
    // mousemove/keypress event.
    const now = Date.now();
    if (now - this.lastActivityCheckTime > 30000) {
      this.lastActivityCheckTime = now;

      // If user is active (hovering, typing, etc.) and token is nearing expiry,
      // refresh it in the background. This prevents the scenario where the user
      // hovers on the screen without triggering any API calls, the token silently
      // expires, and then the next tab click fails because /v1/token returns an
      // empty response (backend session already dead).
      if (!this.sharedService.isInternalApp() && this.tokenService.isTokenExpired() && !this.silentRefreshInProgress && !this.dialogActive && !this.loggingOut) {
        this.refreshTokenSilently();
      }
    }

    // Only start the idle timer if it isn't already running.
    // The timer is set accurately from real token expiry and doesn't need to be
    // reset on every activity — it only needs resetting when expiry actually changes
    // (after a token refresh → refreshTokenSilently/stayLoggedInFn call resetIdleTimer)
    // or after a tab switch (onVisibilityChange calls resetIdleTimer).
    // This avoids needless clearInterval/setInterval/localStorage reads while the
    // user is just hovering with 20 minutes still left on the token.
    if (!this.clearTime) {
      this.resetIdleTimer();
    }
  }

  /**
   * Calculates how much time remains until (tokenExpiry - 3 min) and resets
   * the idle timer to show the dialog at that point. Falls back to legacy
   * userSessionTimeout-based calculation if no token expiry is stored yet.
   */
  private resetIdleTimer() {
    const tokenExpiryStr = this.localStorageService.getExpiryDateTime();
    let idleTimeout: number;

    if (tokenExpiryStr) {
      const tokenExpiry = moment(tokenExpiryStr, this.shellConstant.sessionConstants.calendarFormat);
      const msUntilExpiry = tokenExpiry.diff(moment());
      idleTimeout = Math.max(msUntilExpiry - this.DIALOG_SHOW_BEFORE_EXPIRY_MS, 0);
    } else if (this.sharedService.applicationInfo && this.sharedService.applicationInfo.data
               && this.sharedService.applicationInfo.data.userSessionTimeout) {
      // Fallback: no token expiry stored yet (first load), use legacy config
      idleTimeout = 1000 * 60 * (this.sharedService.applicationInfo.data.userSessionTimeout - 3);
    } else {
      return;
    }

    this.sharedService.sessionTimeoutValue = idleTimeout;

    clearInterval(this.clearTime);
    if (idleTimeout > 0) {
      this.clearTime = setInterval(() => {
        this.showDialog();
      }, idleTimeout);
    }
  }

  /**
   * Silently refreshes the token in the background when the user is active
   * (hovering, typing) but no API calls are being made. After a successful
   * refresh, resets the idle timer with the new token expiry so the dialog
   * timing stays correct.
   */
  private refreshTokenSilently() {
    this.silentRefreshInProgress = true;
    // Clear the old idle timer immediately so its stale interval cannot fire
    // showDialog() while the HTTP call is in flight (race-condition fix).
    if (this.clearTime) {
      clearInterval(this.clearTime);
      this.clearTime = null;
    }
    this.tokenService.getEATToken().subscribe({
      next: (res) => {
        this.silentRefreshInProgress = false;
        if (res && res.data && res.data.token) {
          const getExpiryTime = ((res.data.expiry - 30) / 60);
          const currentDate = new Date(moment().format('YYYY-MM-DDTHH:mm:ss'));
          const setExpiryTime = moment(currentDate).add(getExpiryTime, 'minutes').format('YYYY-MM-DDTHH:mm:ss');
          this.localStorageService.setToken(decodeURIComponent(res.data.token));
          this.localStorageService.setExpiryDateTime(setExpiryTime);
          // Token refreshed — reset the idle timer with the new expiry
          this.resetIdleTimer();
        } else {
          // /v1/token returned 200 but no token — session is dead on the backend
          console.error('Silent token refresh: /v1/token returned no token data');
          this.logOutFn();
        }
      },
      error: (err) => {
        this.silentRefreshInProgress = false;
        // /v1/token call failed — session may be dead or network issue.
        // Log out to be safe rather than leaving the user on a page with
        // an expiring token and hoping the next retry works.
        console.error('Silent token refresh failed:', err);
        this.logOutFn();
      }
    });
  }

  ngOnInit() {
    // When the user returns to this tab after being away, the browser may have
    // throttled or skipped our idle timer (setInterval is heavily throttled in
    // background tabs). We must check token state immediately on tab focus:
    //  - Token expired → force logout now (prevents sending an expired token
    //    to /v1/token on the next API call which causes refresh_token_authentication_error)
    //  - Token nearing expiry (soft-expired) → refresh silently right away
    //  - Token still valid → reset the idle timer from now so the dialog
    //    countdown is accurate again after the browser throttle period
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || this.sharedService.isInternalApp()) {
      return;
    }

    const tokenExpiryStr = this.localStorageService.getExpiryDateTime();
    if (!tokenExpiryStr) {
      return;
    }

    const tokenExpiry = moment(tokenExpiryStr, this.shellConstant.sessionConstants.calendarFormat);
    const msUntilHardExpiry = tokenExpiry.diff(moment());

    if (msUntilHardExpiry <= 0) {
      // Token has actually expired at OAuth level — logout immediately
      // before any API call can be made with the dead token
      this.logOutFn();
    } else if (msUntilHardExpiry <= this.DIALOG_SHOW_BEFORE_EXPIRY_MS && !this.dialogActive) {
      // Past the dialog threshold (less than 3 min left) but the browser throttled
      // our setInterval in the background so the dialog never appeared.
      // Show it now with the accurate remaining time so the user gets a chance
      // to click "Stay Logged In" rather than being silently logged out.
      const remainingSeconds = Math.max(Math.floor(msUntilHardExpiry / 1000), 1);
      this.showDialog(remainingSeconds);
    } else if (this.tokenService.isTokenExpired() && !this.dialogActive) {
      // Soft-expired window (min 25-27) — dialog not yet due, silent refresh is safe
      // so the next API call has a valid token.
      console.log('Tab refocused: token nearing expiry, refreshing silently.');
      this.refreshTokenSilently();
    } else {
      // Token still valid — just reset the idle timer so it fires correctly
      // from now (not from when the browser last ran the throttled interval)
      this.resetIdleTimer();
    }
  }

  showGlobalTimeOutMsg(confirmObj, observer) {
    this.confirmationService.confirm({
      key: 'globalTimeout',
      header: confirmObj.header,
      message: confirmObj.message,
      acceptLabel: this.sharedConstant.globalTimeoutDialog.logOut,
      rejectLabel: this.sharedConstant.globalTimeoutDialog.stayLoggedIn,
      accept: () => {
        observer.next(true);
        observer.complete();
        this.dialogActive = false; // Reset dialog state on close
        this.logOutFn();
      },
      reject: () => {
        observer.next(false);
        observer.complete();
        this.dialogActive = false; // Reset dialog state on close
        this.stayLoggedInFn();
      }
    });

    // Start the countdown timer with a small delay to ensure the dialog is rendered
    // This addresses a race condition where the timer would start before the dialog was fully
    // rendered in the DOM, causing the timer not to display correctly
    setTimeout(() => {
      this.startCountdown();
    }, 100);
  }

  ngOnDestroy() {
    this.clearSetTimeInterval();
    if (this.clearTime) {
      clearInterval(this.clearTime);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Initiates the timeout dialog sequence
   * Resets counters, updates display, and shows the modal
   * Prevents multiple dialogs from appearing simultaneously
   */
  showDialog(countdownSeconds?: number) {
    // Prevent multiple dialogs from being shown simultaneously
    if (this.dialogActive) {
      return;
    }

    // If a silent refresh is in flight, the new token hasn't been stored yet.
    // Don't show the dialog — when the refresh completes it will either:
    // - succeed → store new token + resetIdleTimer (dialog not needed)
    // - fail → logOutFn (dialog not needed)
    if (this.silentRefreshInProgress) {
      return;
    }

    // The idle timer was set based on a previous token's expiry. Since then,
    // the interceptor or silent refresh may have already renewed the token.
    // Check if the dialog is still needed by looking at the CURRENT expiry.
    const tokenExpiryStr = this.localStorageService.getExpiryDateTime();
    let actualRemainingSeconds = 180;
    
    if (tokenExpiryStr) {
      const msUntilExpiry = moment(tokenExpiryStr, this.shellConstant.sessionConstants.calendarFormat).diff(moment());
      if (msUntilExpiry > this.DIALOG_SHOW_BEFORE_EXPIRY_MS) {
        // Token was refreshed since the timer was set — dialog not needed.
        // Reset the idle timer to fire at the new expiry - 3 min.
        this.resetIdleTimer();
        return;
      }
      // CRITICAL FIX: If browser throttled the setInterval, we might be showing the
      // dialog 40 seconds later than intended. We MUST NOT start a fresh 3-minute
      // counter, because that would mean the UI timer goes PAST the actual token expiry!
      actualRemainingSeconds = Math.max(0, Math.floor(msUntilExpiry / 1000));
    }

    this.clearSetTimeInterval();
    const requestedSeconds = countdownSeconds || 180;
    this.counter = Math.min(requestedSeconds, actualRemainingSeconds);
    this.timeleft = this.counter;
    this.updateTimerDisplay();
    this.dialogActive = true;
    this.showTimeoutModal();
  }

  private startCountdown() {
    // Only start a new timer if none is currently running
    // This prevents multiple timers from running simultaneously
    // and ensures clean timer management
    if (!this.countDown) {
      this.countDown = setInterval(() => {
        this.counter--;
        
        let actualRemainingSeconds = this.counter;
        const tokenExpiryStr = this.localStorageService.getExpiryDateTime();
        if (tokenExpiryStr) {
          const tokenExpiry = moment(tokenExpiryStr, this.shellConstant.sessionConstants.calendarFormat);
          actualRemainingSeconds = Math.max(0, Math.floor(tokenExpiry.diff(moment()) / 1000));
        }

        // The visual time left MUST NOT exceed the actual token time remaining.
        // This cures browser background tab throttling (if the JS engine freezes
        // the timer for 30s while the user is in another tab, when they return,
        // the timer instantly jumps down to the correct remaining time so it
        // never outlives the real token).
        this.timeleft = Math.min(Math.max(0, this.counter), actualRemainingSeconds);
        
        this.updateTimerDisplay();
        this.updateModalMessage(); // Update the timer display in the modal
        
        if (this.timeleft <= 0) {
          this.autoLoggedOutUser();
        }
      }, 1000);
    }
  }

  /**
   * Formats the remaining time as a MM:SS display string
   * Updates the showHtmlTimer property for use in the dialog
   */
  private updateTimerDisplay() {
    const minutes = Math.floor(this.timeleft / 60);
    const seconds = Math.floor(this.timeleft - minutes * 60);
    this.showHtmlTimer = (('00' + minutes).slice(-2) + ':' + ('00' + seconds).slice(-2));
  }

  /**
   * Creates and displays the timeout modal with appropriate configuration
   * Sets up an Observable to handle dialog interaction
   */
  showTimeoutModal() {
    const confirmObj = {
      header: this.sharedConstant.globalTimeoutDialog.title,
      message: this.getTimeoutMessage(),
      acceptBtn: this.sharedConstant.globalTimeoutDialog.logOut,
      rejectBtn: this.sharedConstant.globalTimeoutDialog.stayLoggedIn
    };

    new Observable<boolean>((observer) => {
      this.showGlobalTimeOutMsg(confirmObj, observer);
    }).subscribe();
  }

  /**
   * Updates the timer display in the modal dialog
   * Uses two different strategies to ensure the timer is updated correctly:
   * 1. Direct DOM manipulation of span elements (more efficient)
   * 2. Fallback to updating the entire message if spans can't be found
   */
  private updateModalMessage() {
    // update span elements directly for efficiency
    // This targets specific timer span elements for minimal DOM updates
    const timeoutSpans = document.querySelectorAll('span.sdps-timeout');
    if (timeoutSpans && timeoutSpans.length > 0) {
      for (let i = 0; i < timeoutSpans.length; i++) {
        timeoutSpans[i].textContent = this.showHtmlTimer;
      }
      return;
    }

    // if spans can't be found, update the entire message
    // This ensures the timer is updated even if the DOM structure is different
    // Uses multiple selectors to be more robust across different dialog implementations
    const dialogSelector = 'p-confirmdialog[key="globalTimeout"] .p-confirm-dialog-message, .p-dialog-content .p-confirm-dialog-message';
    const element = document.querySelector(dialogSelector);
    if (element) {
      element.innerHTML = this.getTimeoutMessage();
    }
  }

  public getTimeoutMessage(): string {
    return `${this.sharedConstant.globalTimeoutDialog.sessionExpiredMessage}<br><span class="sdps-timeout">${this.showHtmlTimer || '03:00'}</span><br>${this.sharedConstant.globalTimeoutDialog.minsAndSec}<br>${this.sharedConstant.globalTimeoutDialog.message}`;
  }

  logOutFn() {
    this.loggingOut = true; // Prevent any further token refresh attempts
    this.clearSetTimeInterval();
    // Clear the idle timer to prevent showDialog() firing during logout redirect
    if (this.clearTime) {
      clearInterval(this.clearTime);
      this.clearTime = 0;
    }
    this.dialogActive = false;
    this.authService.logOutUser();
  }

  stayLoggedInFn() {
    this.clearSetTimeInterval();
    this.dialogActive = false;

    // Refresh the token to renew the session
    this.tokenService.getEATToken().subscribe({
      next: (res) => {
        if (res && res.data && res.data.token) {
          const getExpiryTime = ((res.data.expiry - 30) / 60);
          const currentDate = new Date(moment().format('YYYY-MM-DDTHH:mm:ss'));
          const setExpiryTime = moment(currentDate).add(getExpiryTime, 'minutes').format('YYYY-MM-DDTHH:mm:ss');
          this.localStorageService.setToken(decodeURIComponent(res.data.token));
          this.localStorageService.setExpiryDateTime(setExpiryTime);
          // Reset the idle timer with the new expiry so the next dialog fires
          // at the correct time (newExpiry - 3min), not on the old stale schedule.
          this.resetIdleTimer();
        } else {
          // Invalid token response, log out user
          this.logOutFn();
        }
      },
      error: () => {
        this.logOutFn();
      }
    });
  }

  clearSetTimeInterval() {
    // Properly clean up timer to prevent memory leaks and multiple timers
    // This ensures timers are completely cleared and the reference is nulled
    // to allow garbage collection and prevent duplicate timers
    if (this.countDown) {
      clearInterval(this.countDown);
      this.countDown = null;
    }
  }

  autoLoggedOutUser() {
    this.clearSetTimeInterval();
    if (this.clearTime) {
      clearInterval(this.clearTime);
      this.clearTime = 0;
    }
    let schwabClientHeader = [];
    schwabClientHeader = this.sharedService.setSchwabClientHeaders('xx', 1, schwabClientHeader);
    this.httpService.delete(this.environment.webAppService + '/v1/logout' + '?Schwab-Client-Ids= ' + schwabClientHeader).subscribe(res => {
      const data = 'data';
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = encodeURI(res[data]);
    });
  }
}
